/**
 * TopBar — sticky 56 px header. Brand omitted (lives in sidebar).
 * Layout: breadcrumb (left) · ⌘K trigger + actions (right).
 * Below md it is compact: hamburger (below lg) · page title · search icon ·
 * notifications · avatar menu (theme moves into the avatar menu).
 *
 * The ⌘K trigger is a button mock-up of the keyboard shortcut so people
 * who don't know the binding can still discover it. Click → opens
 * CommandPalette via UI store.
 */

import { useLocation, Link, useNavigate } from 'react-router-dom'
import { useUiStore } from '@/store/ui'
import { useAuth } from '@/hooks/useAuth'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useBreadcrumbStore } from '@/hooks/useBreadcrumbLabel'
import {
  useNotifications,
  type NotificationRecord,
} from '@/store/notifications'
import { fmtRelative } from '@/lib/format'
import { cn } from '@/lib/utils'
import {
  isInboxMuted,
  setInboxMuted,
  requestInboxNotifications,
} from '@/hooks/useInboxAlerts'
import { notify } from '@/lib/notify'
import { useTeamStore, useTotalUnread } from '@/store/team'

const ROUTE_LABELS: Record<string, string> = {
  '': 'Cockpit',
  inbox: 'Inbox',
  live: 'Live visitors',
  customers: 'Customers',
  subscriptions: 'Subscriptions',
  finance: 'Finance',
  invoices: 'Invoices',
  payments: 'Payments',
  reports: 'Reports',
  wallets: 'Wallets',
  ops: 'Ops',
  services: 'Services',
  logs: 'Logs',
  system: 'System',
  admin: 'Admin',
  users: 'Users',
  audit: 'Audit',
  'service-keys': 'Service keys',
}

function labelFor(seg: string) {
  return ROUTE_LABELS[seg] ?? seg.replace(/-/g, ' ').replace(/^\w/, (c) => c.toUpperCase())
}

export function TopBar() {
  const setCommandPaletteOpen = useUiStore((s) => s.setCommandPaletteOpen)
  const { user, logout } = useAuth()
  const location = useLocation()
  const crumbOverrides = useBreadcrumbStore((s) => s.overrides)

  const segments = location.pathname.split('/').filter(Boolean)
  const crumbs = segments.length === 0
    ? [{ label: 'Cockpit', to: '/' }]
    : segments.map((seg, i) => ({
        label: crumbOverrides[seg] ?? labelFor(seg),
        to: '/' + segments.slice(0, i + 1).join('/'),
      }))

  const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform)

  const setMobileNavOpen = useUiStore((s) => s.setMobileNavOpen)
  const title = crumbs[crumbs.length - 1]?.label ?? 'Cockpit'

  return (
    <header className="h-[var(--topbar-h)] sticky top-0 z-30 border-b border-hairline bg-surface/80 backdrop-blur flex items-center justify-between gap-2 md:gap-4 px-2 sm:px-4 md:px-6">
      {/* Hamburger: opens the off-canvas nav below lg. */}
      <button
        type="button"
        onClick={() => setMobileNavOpen(true)}
        aria-label="Open navigation"
        className="lg:hidden h-11 w-11 shrink-0 -ml-0.5 rounded-md flex items-center justify-center text-ink-700 hover:bg-surface-alt transition-colors cursor-pointer"
      >
        <Icon name="bars" variant="solid" className="text-base" />
      </button>

      {/* Phones: just the page title. */}
      <h1 className="md:hidden flex-1 min-w-0 truncate text-[15px] font-semibold text-ink-900">
        {title}
      </h1>

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="hidden md:flex flex-1 items-center gap-1.5 text-sm min-w-0">
        {crumbs.map((c, i) => (
          <span key={c.to} className="flex items-center gap-1.5 min-w-0">
            {i > 0 && <span className="text-ink-300">/</span>}
            {i === crumbs.length - 1 ? (
              <span className="text-ink-900 font-medium truncate">{c.label}</span>
            ) : (
              <Link
                to={c.to}
                className="text-ink-500 hover:text-ink-900 transition-colors truncate"
              >
                {c.label}
              </Link>
            )}
          </span>
        ))}
      </nav>

      {/* Right cluster */}
      <div className="flex items-center gap-1 md:gap-2 shrink-0">
        <button
          type="button"
          onClick={() => setCommandPaletteOpen(true)}
          className="hidden md:flex items-center gap-2 h-8 px-2.5 pr-1.5 rounded-md border border-hairline text-xs text-ink-500 hover:border-hairline-bold hover:text-ink-900 transition-colors cursor-pointer"
        >
          <Icon name="magnifying-glass" variant="solid" className="text-xs" />
          <span>Search</span>
          <span className="num text-[10px] text-ink-400 border border-hairline rounded px-1 py-px ml-2">
            {isMac ? '⌘K' : 'Ctrl K'}
          </span>
        </button>
        <button
          type="button"
          onClick={() => setCommandPaletteOpen(true)}
          aria-label="Search"
          className="md:hidden h-11 w-11 rounded-md flex items-center justify-center text-ink-700 hover:bg-surface-alt transition-colors cursor-pointer"
        >
          <Icon name="magnifying-glass" variant="solid" className="text-base" />
        </button>

        <div className="hidden md:block">
          <ThemeToggle />
        </div>

        <NotificationMenu />

        <UserMenu email={user?.email} onLogout={logout} />
      </div>
    </header>
  )
}

function ThemeToggle() {
  const theme = useUiStore((s) => s.theme)
  const setTheme = useUiStore((s) => s.setTheme)
  const iconName = theme === 'dark' ? 'moon' : theme === 'light' ? 'sun' : 'display'
  return (
    <Button
      variant="ghost"
      size="icon"
      title={`Theme: ${theme}`}
      onClick={() => {
        const next = theme === 'system' ? 'light' : theme === 'light' ? 'dark' : 'system'
        setTheme(next)
      }}
    >
      <Icon name={iconName} variant="solid" className="text-sm" />
    </Button>
  )
}

function NotificationMenu() {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  const items = useNotifications((s) => s.items)
  const markAllRead = useNotifications((s) => s.markAllRead)
  const markRead = useNotifications((s) => s.markRead)
  const clear = useNotifications((s) => s.clear)

  const unread = useMemo(() => items.filter((i) => !i.read).length, [items])
  const [muted, setMuted] = useState(isInboxMuted())

  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  // Mark everything read when the dropdown is opened (Gmail-style)
  useEffect(() => {
    if (open && unread > 0) {
      // Slight delay so the badge animates down rather than blinking
      const t = setTimeout(() => markAllRead(), 300)
      return () => clearTimeout(t)
    }
  }, [open, unread, markAllRead])

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="Notifications"
        aria-label="Notifications"
        className="relative h-11 w-11 md:h-9 md:w-9 rounded-md text-ink-700 hover:bg-surface-alt transition-colors cursor-pointer flex items-center justify-center"
      >
        <Icon name="bell" variant="duotone" className="text-sm" />
        {unread > 0 && (
          <span
            className="absolute top-1 right-1.5 min-w-[14px] h-[14px] px-1 rounded-full bg-danger text-white text-[9px] font-semibold num flex items-center justify-center leading-none"
            aria-label={`${unread} unread`}
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed inset-x-2 top-[calc(var(--topbar-h)+4px)] sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-80 rounded-md border border-hairline bg-surface-raised py-1 shadow-[var(--shadow-popover)] z-40">
          <header className="flex items-center justify-between px-3 py-2 border-b border-hairline">
            <span className="text-[10px] uppercase tracking-wider text-ink-500 font-semibold">
              Notifications
              {items.length > 0 && (
                <span className="num text-ink-400 ml-1.5">
                  {items.length}
                </span>
              )}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  const next = !muted
                  setInboxMuted(next)
                  setMuted(next)
                }}
                title={muted ? 'Unmute alert sound' : 'Mute alert sound'}
                className={cn(
                  'text-[10px] transition-colors cursor-pointer flex items-center gap-1',
                  muted
                    ? 'text-ink-400 hover:text-ink-900'
                    : 'text-ink-500 hover:text-ink-900',
                )}
              >
                <Icon
                  name={muted ? 'bell-slash' : 'bell'}
                  variant="solid"
                  className="text-[9px]"
                />
                {muted ? 'Muted' : 'Sound on'}
              </button>
              <button
                type="button"
                onClick={async () => {
                  const ok = await requestInboxNotifications()
                  if (ok) notify.success('Desktop notifications enabled')
                  else
                    notify.warn(
                      'Desktop notifications denied',
                      {
                        description:
                          'Unblock at the site-permissions icon in your address bar.',
                      },
                    )
                }}
                title="Let the browser show OS-level toasts when a new message lands"
                className="text-[10px] text-ink-400 hover:text-ink-900 transition-colors cursor-pointer"
              >
                Desktop
              </button>
              {items.length > 0 && (
                <button
                  type="button"
                  onClick={clear}
                  className="text-[10px] text-ink-400 hover:text-ink-900 transition-colors cursor-pointer"
                >
                  Clear all
                </button>
              )}
            </div>
          </header>

          {items.length === 0 ? (
            <div className="px-3 py-8 text-center text-xs text-ink-400">
              <Icon
                name="bell-slash"
                variant="duotone"
                className="text-base mb-2 block mx-auto text-ink-300"
              />
              Nothing yet — notifications land here when things happen.
            </div>
          ) : (
            <ul className="max-h-[70dvh] sm:max-h-[60vh] overflow-y-auto overscroll-contain clean-scroll divide-y divide-hairline">
              {items.map((n) => (
                <NotificationItem
                  key={n.id}
                  n={n}
                  onClick={() => {
                    markRead(n.id)
                    if (n.href) {
                      setOpen(false)
                      navigate(n.href)
                    }
                  }}
                />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

const TONE_META: Record<
  NotificationRecord['tone'],
  { icon: string; color: string; bg: string }
> = {
  success: { icon: 'circle-check', color: 'text-success', bg: 'bg-success/10' },
  info:    { icon: 'circle-info',  color: 'text-primary-ink', bg: 'bg-primary-tint' },
  warn:    { icon: 'triangle-exclamation', color: 'text-warn', bg: 'bg-warn/10' },
  danger:  { icon: 'circle-xmark', color: 'text-danger', bg: 'bg-danger/10' },
}

function NotificationItem({
  n,
  onClick,
}: {
  n: NotificationRecord
  onClick: () => void
}) {
  const meta = TONE_META[n.tone]
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'w-full text-left px-3 py-2.5 flex items-start gap-2.5 hover:bg-surface-alt transition-colors cursor-pointer',
          !n.read && 'bg-primary/5',
        )}
      >
        <div
          className={cn(
            'h-6 w-6 rounded-full flex items-center justify-center shrink-0 mt-0.5',
            meta.bg,
          )}
        >
          <Icon
            name={meta.icon}
            variant="solid"
            className={cn('text-[11px]', meta.color)}
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span
              className={cn(
                'text-sm text-ink-900 truncate',
                !n.read && 'font-medium',
              )}
            >
              {n.title}
            </span>
            {!n.read && (
              <span
                className="h-1.5 w-1.5 rounded-full bg-primary shrink-0"
                aria-label="Unread"
              />
            )}
          </div>
          {n.description && (
            <p className="text-xs text-ink-500 mt-0.5 line-clamp-2 leading-snug">
              {n.description}
            </p>
          )}
          <div className="flex items-center gap-1.5 mt-1 text-[10px] text-ink-400 num">
            <span>{fmtRelative(n.at)}</span>
            {n.source && (
              <>
                <span>·</span>
                <span>{n.source}</span>
              </>
            )}
          </div>
        </div>
      </button>
    </li>
  )
}

function UserMenu({ email, onLogout }: { email?: string; onLogout: () => void }) {
  const [open, setOpen] = useState(false)
  const toggleTeamChat = useTeamStore((s) => s.togglePanel)
  const teamUnread = useTotalUnread()
  const theme = useUiStore((s) => s.theme)
  const setTheme = useUiStore((s) => s.setTheme)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const initials = (email ?? '?').slice(0, 2).toUpperCase()

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Account menu"
        className="h-10 w-10 md:h-8 md:w-8 rounded-md bg-primary-tint text-primary-ink text-xs font-semibold flex items-center justify-center hover:opacity-90 transition cursor-pointer"
        title={email ?? 'Account'}
      >
        {initials}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-56 rounded-md border border-hairline bg-surface-raised py-1 shadow-[var(--shadow-popover)] z-40">
          <div className="px-3 py-2 border-b border-hairline">
            <div className="text-xs text-ink-400 uppercase tracking-wider">Signed in as</div>
            <div className="text-sm text-ink-900 truncate mt-0.5">{email}</div>
          </div>
          <button
            type="button"
            onClick={() => {
              const next = theme === 'system' ? 'light' : theme === 'light' ? 'dark' : 'system'
              setTheme(next)
            }}
            className="md:hidden w-full text-left px-3 py-2.5 text-sm text-ink-700 hover:bg-surface-alt hover:text-ink-900 transition-colors cursor-pointer flex items-center gap-2"
          >
            <Icon
              name={theme === 'dark' ? 'moon' : theme === 'light' ? 'sun' : 'display'}
              variant="solid"
              className="text-xs w-4"
            />
            Theme: {theme}
          </button>
          <button
            type="button"
            onClick={() => {
              setOpen(false)
              toggleTeamChat()
            }}
            className="w-full text-left px-3 py-2.5 md:py-2 text-sm text-ink-700 hover:bg-surface-alt hover:text-ink-900 transition-colors cursor-pointer flex items-center gap-2"
          >
            <Icon name="comments" variant="solid" className="text-xs w-4" />
            Team chat
            {teamUnread > 0 && (
              <span className="ml-auto num min-w-5 h-5 px-1 rounded-full bg-danger text-white text-[10px] font-semibold flex items-center justify-center">
                {teamUnread > 99 ? '99+' : teamUnread}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => {
              setOpen(false)
              onLogout()
            }}
            className="w-full text-left px-3 py-2.5 md:py-2 text-sm text-ink-700 hover:bg-surface-alt hover:text-ink-900 transition-colors cursor-pointer"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  )
}
